import {
  openAudioStream,
  closeAudioStream,
  addRecordingFromAudioBuffer,
  collectAudioBuffers,
  mergeAndInterleaveRecording,
  createWavBytes,
  convertFloatTo16BitPCM
} from './audioUtils';

import http from './http';

import Accessor from './audioUtils/stTextAccessor';
import get from 'lodash-es/get';
import isEqual from 'lodash-es/isEqual';

export default class TranscriberAPI {

  constructor(host, optionsPath) {

    this._host = host;
    this._optionsPath = optionsPath;
    
    host.on('destroy', () => {
      this.end();
    });

    this.applyOptions();

    host.on('init', () => {
      this.applyOptions();
    });


    this._sendChannelPcmBytesPromise = null;
    this._sendChannelPcmBytesExecutingCount = 0;
    this._sendChannelPcmBytesPendingCount = 0;
    this._lastSyncTime = Date.now();    

    this._reset();
  }

  get state(){
    if(this._stopped) {return 'stopped';}
    else if(this._paused) {return 'paused';}
    else {return 'started';}
  }

  applyOptions() {

    const options = get(this._host,this._optionsPath,{});

    if(!this._options || this._options.audioBufferSize!==options.audioBufferSize){
      this._audioBufferSize = options.audioBufferSize||0;
    }

    if(!this._options || this._options.syncInterval!==options.syncInterval){
      this._syncInterval = options.syncInterval||0;
    }

    if(!this._options || this._options.syncTranscript!==options.syncTranscript){
      this._syncTranscript = options.syncTranscript||false;
    }

    if(!this._options || this._options.pauseOnSync!==options.pauseOnSync){
      this._pauseOnSync = options.pauseOnSync||false;
    }

    if(!this._options || this._options.serviceName!==options.serviceName){
      this._serviceName = options.serviceName||'Default';
    }

    if(!this._options || this._options.nodeName!==options.nodeName){
      this._nodeName = options.nodeName||'';
    }

    if(!this._options || this._options.downloadAudio!==options.downloadAudio){
      this._downloadAudio = options.downloadAudio||false;
    }

    if(!this._options || this._options.chunkSeparator!==options.chunkSeparator){
      this._chunkSeparator = options.chunkSeparator||'';
    }

    if(!this._options || this._options.acceptorURL!==options.acceptorURL){
      this._acceptorURL = options.acceptorURL||'';
    }

    if(!this._options || this._options.apiKey!==options.apiKey){
      this._apiKey = options.apiKey||'';
    }

    if(!this._options || this._options.conversationId!==options.conversationId){
      this._conversationIdOption = options.conversationId;
    }

    if(!this._options || this._options.conversationType!==options.conversationType){
      this._type = options.conversationType;
    }

    if(!this._options || this._options.transcriptFilePath!==options.transcriptFilePath){
      this._transcriptFilePath = options.transcriptFilePath;
    }

    if(!this._options || this._options.wavFilePath!==options.wavFilePath){
      this._wavFilePath = options.wavFilePath;
    }

    if(!this._options || this._options.explain!==options.explain){
      this._explain = options.explain;
    }

    if(!this._options || this._options.appendTranscriptFile!==options.appendTranscriptFile){
      this._appendTranscriptFile = options.appendTranscriptFile;
    }

    if(!this._options || this._options.notifyInterval!==options.notifyInterval){
      this._notifyInterval = options.notifyInterval;
    }

    if(!this._options || this._options.notifyWordCount!==options.notifyWordCount){
      this._notifyWordCount = options.notifyWordCount;
    }

    if(!this._options || this._options.noTextReply!==options.noTextReply){
      this._noTextReply = options.noTextReply;
    }

    if(!this._options || this._options.textFullReply!==options.textFullReply){
      this._textFullReply = options.textFullReply;
    }
    
    if(!this._options || !isEqual(this._options.punctuationParams,options.punctuationParams)){
      this._punctuationParams = options.punctuationParams;
    }


    
    this._options = options;
    this._onOptionsChange(options);
  }

  pause(){
    if(!this._paused){
      this._paused = Date.now();
      this._onPaused();
    }
  }

  resume(){
    if(this._paused){
      this._recordingLast.lastSubmitTime+=Date.now()-this._paused;
      this._recordingLast.lastSendTime = this._recordingLast.lastSubmitTime;
      this._paused = false;
      this._onResumed();
    }
  }

  start() {
    if(!this._accessor){
      this._accessor = new Accessor(this._nodeName, this._serviceName, uri => {

        if(this._acceptorURL){
          uri = this._acceptorURL+uri;
        }

        if(this._apiKey){
          return http.auth(uri,undefined,this._apiKey);
        }else{
          return http.auth(uri,this._host.args.auth,this._host.args.token);              
        }
      });
    }

    if (this._conversationId) {
        // this._onError(new Error(`Transcription already started, id: ${this._}`));
      return Promise.resolve();
    } else {
      const params = {};
      if(this._conversationIdOption!==undefined){
        params.conversationId = this._conversationIdOption;
      }

      if(this._type!==undefined){
        params.type = this._type;
      }

      if(this._transcriptFilePath!==undefined){
        params.transcriptFilePath = this._transcriptFilePath;
      }

      if(this._wavFilePath!==undefined){
        params.wavFilePath = this._wavFilePath;
      }

      if(this._explain!==undefined){
        params.explain = this._explain;
      }

      if(this._appendTranscriptFile!==undefined){
        params.appendTranscriptFile = this._appendTranscriptFile;
      }

      if(this._notifyInterval!==undefined){
        params.notifyInterval = this._notifyInterval;
      }

      if(this._notifyWordCount!==undefined){
        params.notifyWordCount = this._notifyWordCount;
      }

      if(this._punctuationParams!==undefined){
        params.punctuationParams = this._punctuationParams;
      }
      
      return this._accessor.startTranscription(params).then(response => {
                // console.log(`startTranscription: response: ${JSON.stringify(response)}`);
        this._conversationId = response.conversationId;                        
        this._stopped = false;
        this._audioFormat = JSON.parse(JSON.stringify(response.audioFormatSuggested));
        this._onText('','');
        this._onStarted(response);
        this._onResponse(response);

        openAudioStream(this._conversationId, 1024*2, this._audioFormat.channels, this._audioFormat.sampleRate, (audioBuffer) => {
          if(this._paused) {return;}

          addRecordingFromAudioBuffer(this._conversationId, audioBuffer);

          collectAudioBuffers(this._recordingLast, audioBuffer);

          if (this._audioBufferSize && (Date.now() - this._recordingLast.lastSubmitTime > this._audioBufferSize*1000)) {
            this._sendChannelPcmBytes();
          }
        }).then(r => {
                    // console.log(`Audio stream ${conversationId}: stream opened`);
          this._audioFormat.sampleRate = r.recording.sampleRate;
          this._audioFormat.frameRate = r.recording.sampleRate;
        }).catch(error => {
          this._onError(error);
                  // console.error(error);
          return this.end();
        })

      })
      .catch(error => {
        this._onError(error);
      });
    }
  }

  end(){
    return this.stop();
  }

  stop() {
    if (this._conversationId) {
      this._stopped = true;
      return this._sendChannelPcmBytes(true).then(_ => {
        const params = {
          conversationId:this._conversationId,          
        };
        if(this._textFullReply!==undefined){
          params.textFullReply = this._textFullReply;
        }

        return Promise.all(
          [
            this._closeAudioStreamAndSaveWavFile(),
            this._accessor.endTranscription(params)
            .then(response => {

              this._onResponse(response);
              this._onText(response.punctuatedText&&response.punctuatedText.text||'',response.punctuatedTextFull&&response.punctuatedTextFull.text||'');
            })
            .catch(error => {
              this._onError(error);                                    
            })
            ]
          ).then(response => {
            this._reset();
            this._onStopped(response[1]);
          });
        });
    }else{
      return Promise.resolve();
    }
  }

  send(){
    return this._sendChannelPcmBytes();
  }

  sync(){
    this._sync=true;
  }

  get stopped(){
    return this._stopped;
  }

  _reset() {
    this._conversationId = null;
    this._audioFormat = null;
    this._recordingLast = {
      channel0: [],
      channel1: [],
      length: 0,
      lastSendTime: Date.now(),
      lastSubmitTime: Date.now(),
    };
    this._firstTimeSend = true;
    this._stopped = true;
    this._paused = false;
    this._sync = false;
  }

  _saveAudioToWavFile(recordingFull) {
    if (this._downloadAudio && recordingFull) {
      const wavBytes = createWavBytes(this._conversationId, recordingFull);

      if (wavBytes)
      {
        const wav = new Blob([wavBytes], {type: 'audio/wav'})
        const a = document.createElement('a');
        a.setAttribute('href', URL.createObjectURL(wav));
        a.setAttribute('download', `wav-${this._conversationId}.wav`);
        a.style.display='none';
        document.body.appendChild(a);  
        a.click();
        a.remove();              
      }
    }

    if(recordingFull){
      recordingFull.length = 0;        
    }

    return Promise.resolve();
  }

  _closeAudioStreamAndSaveWavFile() {
    try {
      return closeAudioStream(this._conversationId).then(recordingFull => {
        return this._saveAudioToWavFile(recordingFull);
      });
    } catch (error) {        
      this._onError(error);
    }
  }

  _sendChannelPcmBytes(force = false) {
    if(this._conversationId){

      if (!this._stopped || force) {

        if (this._sendChannelPcmBytesExecutingCount == 0) {
          this._sendChannelPcmBytesPromise = null;
        }

        if (this._recordingLast.channel0.length == 0)
        {
          this._sendChannelPcmBytesPendingCount = 0;
          return this._sendChannelPcmBytesPromise || Promise.resolve();
        }

        this._recordingLast.lastSubmitTime = Date.now();
        this._sendChannelPcmBytesPendingCount++;
        if (this._sendChannelPcmBytesExecutingCount > 0) {
                // console.log(`Audio stream ${conversationId}: pending send added, pending count: ${sendChannelPcmBytesPendingCount}`);
          return this._sendChannelPcmBytesPromise || Promise.resolve();
        }

        const recordingBytes = mergeAndInterleaveRecording(this._recordingLast);

        const recordingBytesPcm = convertFloatTo16BitPCM(recordingBytes)
        this._recordingLast.channel0 = [];
        this._recordingLast.channel1 = [];
        this._recordingLast.length = 0;
        this._recordingLast.lastSendTime = Date.now();

        let sync = this._syncTranscript || this._sync;
        this._sync = false;

        const syncInterval = this._syncInterval*1000;
        const audioBufferSize = this._audioBufferSize*1000;

        if (sync) {
          this._lastSyncTime = Date.now();
        } else if (syncInterval && (syncInterval == audioBufferSize || this._lastSyncTime + syncInterval < Date.now())) {
          sync = true;
          this._lastSyncTime = Date.now();
        }

        if(sync&&this._pauseOnSync){
          this.pause();
        }

        const request = {
          conversationId:this._conversationId,
          bytes: Array.from(new Uint8Array(recordingBytesPcm)),
          audioFormat:this._audioFormat,
          sync,
        };
        if(this._noTextReply!==undefined){
          request.noTextReply = this._noTextReply;
        }


        this._sendChannelPcmBytesExecutingCount = this._sendChannelPcmBytesPendingCount;
        this._sendChannelPcmBytesPendingCount = 0;
        const promise = this._accessor.submitWavBytes(request).then(r => {
          this.resume();
          this._sendChannelPcmBytesExecutingCount = 0;
                // console.log(`Audio stream ${conversationId}: response from audio server: ${JSON.stringify(r)}`);
          this._onResponse(r);
          this._onText(r.punctuatedText&&r.punctuatedText.text||'',r.punctuatedTextFull&&r.punctuatedTextFull.text||'');               


          if (r.isConversationComplete) {
                    // console.log(`Audio stream ${conversationId}: stream was closed on backed`);
            return this._closeAudioStreamAndSaveWavFile().then(_ => {this._reset();});
          }

          if (this._sendChannelPcmBytesPendingCount > 0) {
            return this._sendChannelPcmBytes(false);
          }
        }).catch(error => {
          this._sendChannelPcmBytesExecutingCount = 0;
                // console.error(`Audio stream ${conversationId}: failed to submit bytes: ${error}`);
          this._onError(error);

          if (this._sendChannelPcmBytesPendingCount > 0) {
            return this._sendChannelPcmBytes(false);
          }
        });

        if (this._sendChannelPcmBytesPromise == null) {
          this._sendChannelPcmBytesPromise = promise;
        }

        return this._sendChannelPcmBytesPromise;
      }
    } else {
      this._onError(new Error('sendChannelPcmBytes: conversationId is null'));
    }
  }

  _onText(text, textFull){
    this._host.fire('transcriberText', {text:text+this._chunkSeparator, textFull, transcriber: this});
  }

  _onResponse(response){
    this._host.fire('transcriberResponse', {response, transcriber: this});
  }

  _onError(error){
    this._host.fire('transcriberError', {error, transcriber: this});
  }

  _onStarted(response){
    this._host.fire('transcriberStart', {response,conversationId:this._conversationId, transcriber: this});
    this._onStateChanged();
  }

  _onStopped(response){
    this._host.fire('transcriberStop', {response, transcriber: this});
    this._onStateChanged();
  }

  _onPaused(){
    this._host.fire('transcriberPause', {transcriber: this});
    this._onStateChanged();
  }

  _onResumed(){
    this._host.fire('transcriberResume', {transcriber: this});
    this._onStateChanged();
  }

  _onStateChanged(){
    this._host.fire('transcriberState', {state:this.state ,transcriber: this});
  }

  _onOptionsChange(options){
    this._host.fire('transcriberOptionsChange', {options, transcriber: this});
  }

}